import type { HomePageEndpointResponse } from '@ps/isp-web-services-types';
import { FeatureFlag } from '@ps/web-core-enums';

import { type WidgetEffectParams } from '@swf/core/store';
import type { BasicSportsWidgetConfig } from '@swf/sports/pages/types';
import { sportsWidgetPollingBehavior } from '@swf/sports/utils/actions';
import buildSportsRequest from '@swf/sports/utils/buildSportsRequest';
import { POLLING_INTERVAL } from '@swf/sports/utils/constants';
import { getWidgetCancellationSignal } from '@swf/sports/utils/getWidgetCancellationSignal';
import { hasRequestAborted } from '@swf/sports/utils/hasRequestAborted';

import type { WidgetDispatch, WidgetSelect } from './types';

export const effects = {
  async preload(_payload: undefined, params: WidgetEffectParams<BasicSportsWidgetConfig>) {
    const { dispatch, select, rootState, widget } = params;
    const { clear, homePageRequested }: WidgetDispatch = dispatch[widget.uid];
    const { hasPreloaded }: WidgetSelect = select[widget.uid];

    sportsWidgetPollingBehavior(params, {
      pollingInterval: POLLING_INTERVAL
    }).subscribe({
      next: () => {
        homePageRequested();
      },
      complete: () => {
        clear();
      }
    });

    if (!hasPreloaded(rootState)) {
      await homePageRequested();
    }
  },
  async homePageRequested(
    _payload: undefined,
    params: WidgetEffectParams<BasicSportsWidgetConfig>
  ) {
    const { dispatch, request, widget, rootState } = params;
    const sportsRequest = buildSportsRequest(request, rootState);
    const { homePageLoadSuccess, homePageLoadFailure }: WidgetDispatch = dispatch[widget.uid];

    try {
      const { body } = await sportsRequest<HomePageEndpointResponse>({
        path: '/home-page',
        includeFeatures: [
          FeatureFlag.ISPEnableHomePagePopularAccumulators,
          FeatureFlag.ISPEnableHomePagePopularBetBuilders,
          FeatureFlag.ISPEnableLiveStreaming,
          FeatureFlag.ISPReleaseHomePageInPlayFeature,
          FeatureFlag.ISPReleaseHomePageUpcomingEventsFeature,
          FeatureFlag.ISPEnablePopularSportsPicker,
          FeatureFlag.ISPEnableStarSub,
          FeatureFlag.ISPEnableHomepageComponentOrdering
        ],
        signal: getWidgetCancellationSignal(params)
      });

      homePageLoadSuccess(body);
    } catch (error) {
      if (hasRequestAborted(error)) return;
      homePageLoadFailure();
    }
    return;
  }
};
